import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Tooltip } from 'grab-ui';
import I18n from 'i18n-js';
import _isEmpty from 'lodash/isEmpty';
import { getUserCountryCode } from 'helper/user';
import {
  extractOrderIDs,
  MAX_ORDERS_GUEST_ORDER_TRACKING,
  redirectGuestOrderTracking,
} from 'helper/guest-order-tracking';
import { isOnGoingOrderCancellable } from 'helper/history-order/history-order';
import { ongoingBookingSelectionSelector } from 'selectors/new-history-selectors';
import {
  userInfoSelector,
  enableWebCancellationSelector,
} from 'selectors/app-selector';
import printIcon from 'public/icons/print.svg';
import trackIcon from 'public/icons/trackBooking.svg';
import cancelIcon from 'public/icons/cancel.svg';
import { usePrintLabels } from './common-hooks';
import styles from './new-booking-table-page.less';

export const TooltipContent = () => {
  const userInfo = useSelector(userInfoSelector);

  const helpCenterLink = useMemo(
    () => {
      const countryCode = getUserCountryCode(userInfo);
      return I18n.t(
        `v2.bookingsPage.cancelBookingHelpLink.${countryCode.toLowerCase()}`,
      );
    },
    [userInfo],
  );

  return (
    <p>
      {I18n.t('v2.bookingsPage.tooltip.cancelBookingTip')}
      <a href={helpCenterLink} target="_blank">
        {I18n.t('appSider.contactUs')}
      </a>
    </p>
  );
};

const OngoingBookingAction = ({ startCancellation }) => {
  const ongoingBookingSelection = useSelector(ongoingBookingSelectionSelector);
  const enableWebCancellation = useSelector(enableWebCancellationSelector);

  const disableCancelSelection = useMemo(
    () => {
      if (_isEmpty(ongoingBookingSelection)) return true;
      return ongoingBookingSelection.some(
        order => !isOnGoingOrderCancellable(order),
      );
    },
    [ongoingBookingSelection],
  );

  const printLabels = usePrintLabels({
    orders: ongoingBookingSelection,
    isBulk: true,
  });

  const selectedOrderIDs = useMemo(
    () => extractOrderIDs(ongoingBookingSelection),
    [ongoingBookingSelection],
  );

  return (
    ongoingBookingSelection &&
    ongoingBookingSelection.length > 0 && (
      <div className={styles.ongoingBookingAction}>
        <span className={styles.selectedOrderCount}>
          {I18n.t(
            ongoingBookingSelection.length > 1
              ? 'v2.bookingsPage.selectedBookingsPlural'
              : 'v2.bookingsPage.selectedBookingSingle',
            { num: ongoingBookingSelection.length },
          )}
        </span>
        <div className={styles.actions}>
          <Button className={styles.commonAction} onClick={printLabels}>
            <img src={printIcon} alt="" />
            {I18n.t('v2.bookingsPage.printLabels')}
          </Button>
          <Button
            className={styles.commonAction}
            disabled={
              _isEmpty(selectedOrderIDs) ||
              selectedOrderIDs.length > MAX_ORDERS_GUEST_ORDER_TRACKING
            }
            onClick={() => redirectGuestOrderTracking(selectedOrderIDs)}
          >
            <img src={trackIcon} alt="" />
            {I18n.t('v2.bookingsPage.trackDelivery')}
          </Button>
          {enableWebCancellation ? (
            <Tooltip
              placement={'bottomRight'}
              title={disableCancelSelection ? <TooltipContent /> : null}
            >
              <Button
                className={styles.commonAction}
                onClick={() => startCancellation(ongoingBookingSelection, true)}
                disabled={disableCancelSelection}
              >
                <img src={cancelIcon} alt="" />
                {I18n.t('v2.bookingsPage.cancelBookings')}
              </Button>
            </Tooltip>
          ) : null}
        </div>
      </div>
    )
  );
};

export default OngoingBookingAction;
